<template>
  <NotificationMessage
    data-cy="contacts-popup"
    size="medium"
    :show-close-button="false"
    :show-outside-close-button="false"
    class="contacts-popup">
    <h3>
      {{ $t('global.contact_id_missing_title') }}
    </h3>
    <h4>
      {{ $t('global.contact_id_missing_subtitle') }}
    </h4>
    <div class="form full-fields">
      <div class="form-grid">
        <SalesRepContactSelection
          :userContacts="userContacts"
          :selectedContactId="selectedContactId"
          @update:selectedContactId="selectedContactId = $event" />
      </div>

      <div class="buttons-cnt buttons-cnt-divisor full-width">
        <div class="fields">
          <div class="field">
            <BackstageButton
              data-cy="save-button-cnt"
              data-cy-button="save-button"
              :disabled="!selectedContactId"
              full-width
              v-on:click="updateDraftContact">
              {{ $t('global.save_button') }}
            </BackstageButton>
          </div>
        </div>
      </div>
    </div>
  </NotificationMessage>
</template>

<script lang="ts" setup>
import BackstageButton from '@/components/common/BackstageButton.vue';
import NotificationMessage from '@/components/common/NotificationMessage.vue';
import { useNewRelic } from '@/composables/useNewRelic';
import { useOrderManagement } from '@/composables/useOrderManagement';
import { useOrderValidation } from '@/composables/useOrderValidation';
import type { OrderEditType } from '@/constants/orderEditType';
import { useOrderStore } from '@/stores/order';
import { useProductMatrixStore } from '@/stores/product-matrix';
import type { Draft } from '@/types/draft.interface';
import type { ContactModel } from '@onrunning/backstage-models';
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import SalesRepContactSelection from '../common/SalesRepContactSelection.vue';

const selectedContactId = ref<string | undefined>(undefined);
const orderStore = useOrderStore();
const router = useRouter();
const newRelic = useNewRelic();
const { matrixOrders } = useProductMatrixStore();

const props = defineProps<{
  userContacts: ContactModel[];
  editType?: OrderEditType;
}>();

const emit = defineEmits<{
  contactUpdated: [];
}>();

const { isNewDraft } = useOrderValidation(props.editType);

const { updateDraft } = useOrderManagement({ editType: props.editType, contactId: selectedContactId });

const updateDraftContact = async () => {
  if (selectedContactId.value) {
    // for new drafts, redirect to create order route with selected contactId
    if (isNewDraft.value) {
      const currentRoute = router.currentRoute.value;
      router.push({
        name: 'create-order-for-sales-rep',
        params: {
          orderType: currentRoute.params.orderType,
          startDate: currentRoute.params.startDate,
          contactId: selectedContactId.value,
          step: currentRoute.params.step,
        },
      });
      emit('contactUpdated');

      return;
    }

    // for existing drafts, update contactId and save
    for (const order of orderStore.orders.values()) {
      order.contactId = selectedContactId.value;
      const matrixOrder = matrixOrders.find((mo) => mo.orderNumber === order.orderNumber);
      if (matrixOrder) {
        await updateDraft(order as Draft, matrixOrder);
        orderStore.setUpdatedOrder(order as Draft);
      } else {
        newRelic.noticeError(
          new Error(`#updateDraftContact Matrix order not found for order number: ${order.orderNumber}`),
        );
      }
    }

    emit('contactUpdated');
  }
};
</script>

<style scoped lang="scss">
.contacts-popup {
  h3 {
    padding-bottom: 16px;
    text-align: left;
    font-size: 33px;
  }
  h4 {
    margin-top: 0;
    margin-bottom: 20px;
    text-align: left;
    font-size: 20px;
    font-weight: 400;
    color: var(--dark-gray-color);
  }

  .buttons-cnt {
    margin-top: 0 !important;
  }
}
</style>
